import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Avatar, 
  CircularProgress, 
  Chip, 
  Rating,
  useTheme,
  useMediaQuery,
  Stack,
  Paper,
  Button,
  alpha
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Feedback as FeedbackIcon,
  ShoppingCart as OrderIcon,
  ArrowBack as BackIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import api from '../api';
import config from '../config';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const UserFeedbackDetails = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [ordersMap, setOrdersMap] = useState({});
  const [orderLists, setOrderLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/all-data'),
      api.get(`${config.API_BASE_URL}/feedback/user/${userId}`)
    ])
      .then(([allDataRes, feedbackRes]) => {
        const userData = allDataRes.data.users.find(u => u.Id === parseInt(userId));
        setUser(userData);
        setFeedbacks(feedbackRes.data);
        const orders = allDataRes.data.orders.filter(order => order.CustomerId === parseInt(userId));
        const ordersByOrderId = {};
        orders.forEach(order => { ordersByOrderId[order.OrderId] = order; });
        setOrdersMap(ordersByOrderId);
        
        // ✅ DEBUG: Log orderLists to see structure
        const lists = allDataRes.data.orderLists || [];
        console.log('OrderLists Sample:', lists[0]); // See what fields exist
        setOrderLists(lists);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading data:', err);
        setLoading(false);
      });
  }, [userId]);

  // ✅ FIXED: Product Image URL Helper - Parse JSON string first
// ✅ ROBUST FIX: Handle truncated/malformed JSON strings
const getProductImageUrl = (item) => {
  let imageUrl = item?.ProductImage || 
                 item?.productImage || 
                 item?.ImageUrl || 
                 item?.imageUrl || 
                 item?.Image || 
                 item?.image ||
                 item?.productImageUrl ||
                 item?.ProductImageUrl;

  if (!imageUrl) return null;

  // ✅ Parse JSON string if needed (with error handling for truncated JSON)
  if (typeof imageUrl === 'string' && (imageUrl.startsWith('{') || imageUrl.startsWith('['))) {
    try {
      imageUrl = JSON.parse(imageUrl);
    } catch (e) {
      console.warn('Failed to parse image JSON, attempting to extract URL:', e.message);
      
      // ✅ Fallback: Try to extract a valid URL from the malformed JSON string
      const urlMatch = imageUrl.match(/https?:\/\/[^\s"'}]+\.(jpg|jpeg|png|gif|webp)/i);
      if (urlMatch) {
        console.log('✅ Extracted URL from malformed JSON:', urlMatch[0]);
        return urlMatch[0];
      }
      
      // Try to find thumbnail, medium, or any image URL
      const thumbnailMatch = imageUrl.match(/"thumbnail"\s*:\s*"(https?:\/\/[^"]+)"/i);
      if (thumbnailMatch) {
        console.log('✅ Extracted thumbnail URL:', thumbnailMatch[1]);
        return thumbnailMatch[1];
      }
      
      const mediumMatch = imageUrl.match(/"medium"\s*:\s*"(https?:\/\/[^"]+)"/i);
      if (mediumMatch) {
        console.log('✅ Extracted medium URL:', mediumMatch[1]);
        return mediumMatch[1];
      }
      
      console.error('❌ Could not extract any valid URL from malformed JSON');
      return null;
    }
  }

  // Handle object format
  if (typeof imageUrl === 'object' && imageUrl !== null && !Array.isArray(imageUrl)) {
    return imageUrl.medium || 
           imageUrl.thumbnail || 
           imageUrl.small || 
           imageUrl.original || 
           imageUrl.large || 
           null;
  }

  // Handle direct URL strings
  if (typeof imageUrl === 'string') {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    } else if (imageUrl.startsWith('/images/') || imageUrl.startsWith('/uploads/')) {
      return `${config.API_BASE_URL}${imageUrl}`;
    } else {
      return `${config.API_BASE_URL}/${imageUrl}`;
    }
  }

  return null;
};
  // Helper to get initials
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={isMobile ? 40 : 50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 4 } }}>
      {/* Back Button */}
      <Button
        startIcon={<BackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
        onClick={() => navigate('/admin/feedback')}
        sx={{ 
          mb: { xs: 2, sm: 3 },
          fontSize: { xs: '0.85rem', sm: '0.9rem' }
        }}
      >
        {t('BackToFeedbacks') || 'Back to Feedbacks'}
      </Button>

      {/* User Info Card */}
      {user && (
        <Paper
          elevation={3}
          sx={{ 
            p: { xs: 2, sm: 3 },
            mb: { xs: 3, sm: 4 },
            borderRadius: { xs: 2, sm: 3 },
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`
          }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'center', sm: 'flex-start' }}
            spacing={{ xs: 2, sm: 3 }}
            textAlign={{ xs: 'center', sm: 'left' }}
          >
            <Avatar 
              sx={{ 
                width: { xs: 80, sm: 96 }, 
                height: { xs: 80, sm: 96 },
                bgcolor: 'primary.main',
                fontSize: { xs: '2rem', sm: '2.5rem' },
                fontWeight: 700,
                boxShadow: 3,
                border: '4px solid',
                borderColor: 'white'
              }}
            >
              {getInitials(user.FullName)}
            </Avatar>
            <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Typography 
                variant="h5" 
                fontWeight={700}
                sx={{ 
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                {user.FullName || t('NoName') || 'Anonymous User'}
              </Typography>
              <Stack spacing={{ xs: 0.8, sm: 1 }}>
                <Stack 
                  direction="row" 
                  alignItems="center" 
                  spacing={1}
                  justifyContent={{ xs: 'center', sm: 'flex-start' }}
                >
                  <EmailIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'info.main' }} />
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    {user.Email || t('NoEmail') || 'No email'}
                  </Typography>
                </Stack>
                {user.Phone && (
                  <Stack 
                    direction="row" 
                    alignItems="center" 
                    spacing={1}
                    justifyContent={{ xs: 'center', sm: 'flex-start' }}
                  >
                    <PhoneIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'success.main' }} />
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                    >
                      {user.Phone}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
            <Chip
              icon={<FeedbackIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
              label={`${feedbacks.length} ${t('Feedbacks') || 'Feedbacks'}`}
              color="primary"
              sx={{ 
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                height: { xs: 28, sm: 32 },
                fontWeight: 600
              }}
            />
          </Stack>
        </Paper>
      )}

      {/* Feedbacks Section Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          bgcolor: alpha(theme.palette.info.main, 0.05),
          borderLeft: '4px solid',
          borderColor: 'info.main',
          borderRadius: 1
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <StarIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: 'warning.main' }} />
          <Typography 
            variant="h6" 
            fontWeight={600}
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            {t('CustomerFeedbacks') || 'Customer Feedbacks'}
          </Typography>
        </Stack>
      </Paper>

      {/* Feedback Cards */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {feedbacks.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
              <FeedbackIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'grey.300', mb: 2 }} />
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                {t('NoFeedbacksFound') || 'No feedbacks found'}
              </Typography>
            </Box>
          </Grid>
        ) : (
          [...feedbacks]
            .sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt))
            .map(fb => {
              const order = fb.OrderId ? ordersMap[fb.OrderId] : null;
              
              return (
                <Grid item xs={12} md={6} key={fb.Id}>
                  <Card 
                    sx={{
                      boxShadow: 3,
                      borderRadius: { xs: 2, sm: 3 },
                      background: 'linear-gradient(135deg, #f8fafc 60%, #e3f2fd 100%)',
                      transition: 'all 0.3s',
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      '&:hover': { 
                        boxShadow: 6, 
                        transform: isMobile ? 'none' : 'translateY(-4px)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      {/* Rating Header */}
                      <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        spacing={{ xs: 1.5, sm: 0 }}
                        mb={2}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Rating 
                            value={fb.Rating} 
                            readOnly 
                            precision={1} 
                            max={5} 
                            sx={{ fontSize: { xs: 20, sm: 24 } }}
                          />
                          <Typography 
                            variant="body1" 
                            fontWeight={600}
                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                          >
                            {fb.Rating} / 5
                          </Typography>
                        </Stack>
                        <Chip 
                          icon={<TimeIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                          label={new Date(fb.CreatedAt).toLocaleString()} 
                          size="small" 
                          color="primary"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            height: { xs: 24, sm: 26 }
                          }}
                        />
                      </Stack>

                      {/* Comment */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          bgcolor: 'white',
                          borderRadius: 2,
                          mb: 2,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          minHeight: { xs: 60, sm: 80 }
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          color="text.primary"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {fb.Comment || t('NoComment') || 'No comment provided'}
                        </Typography>
                      </Paper>

                      {/* Order Details */}
                      {order && (
                        <Box 
                          sx={{ 
                            p: { xs: 1.5, sm: 2 }, 
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: alpha(theme.palette.info.main, 0.2)
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                            <OrderIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'info.main' }} />
                            <Typography 
                              variant="subtitle2" 
                              fontWeight={700} 
                              color="info.main"
                              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                            >
                              {t('OrderDetails') || 'Order Details'}
                            </Typography>
                          </Stack>

                          <Stack 
                            direction="row" 
                            flexWrap="wrap" 
                            gap={{ xs: 0.8, sm: 1 }} 
                            mb={1.5}
                          >
                            <Chip 
                              label={`#${order.OrderId || order.Id}`}
                              color="info" 
                              size="small"
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 22, sm: 24 },
                                fontWeight: 600
                              }}
                            />
                            <Chip 
                              label={order.OrderStatus}
                              color="success" 
                              size="small"
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 22, sm: 24 }
                              }}
                            />
                            <Chip 
                              label={`${order.TotalAmount} ${order.Currency || 'YER'}`}
                              color="default" 
                              size="small"
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 22, sm: 24 },
                                fontWeight: 600
                              }}
                            />
                            <Chip 
                              icon={<TimeIcon sx={{ fontSize: 12 }} />}
                              label={new Date(order.CreatedDate).toLocaleString()} 
                              size="small"
                              sx={{ 
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                height: { xs: 22, sm: 24 }
                              }}
                            />
                          </Stack>

                          {/* ✅ FIXED: Order Items - ALWAYS SHOW BOX */}
                          {order.OrderId && (() => {
                            const items = orderLists.filter(ol => ol.OrderId === order.OrderId);
                            
                            console.log('Order Items for OrderId', order.OrderId, ':', items);
                            
                            // ✅ ALWAYS render the section, even if no items
                            return (
                              <Box sx={{ mt: 2 }}>
                                <Box sx={{ 
                                  height: '1px', 
                                  bgcolor: 'divider', 
                                  mb: 1.5 
                                }} />
                                <Typography 
                                  variant="subtitle2" 
                                  fontWeight={600} 
                                  mb={1.5} 
                                  color="secondary"
                                  sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                                >
                                  {t('OrderItems') || 'Order Items'} ({items.length})
                                </Typography>
                                
                                {items.length === 0 ? (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      textAlign: 'center',
                                      bgcolor: 'grey.50',
                                      borderRadius: 2
                                    }}
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      {t('NoItemsFound') || 'No items found for this order'}
                                    </Typography>
                                  </Paper>
                                ) : (
                                  <Stack spacing={{ xs: 1, sm: 1.5 }}>
                                    {items.map((item, idx) => {
                                      const productImageUrl = getProductImageUrl(item);

                                      return (
                                        <Paper
                                          key={item.Id || idx}
                                          elevation={0}
                                          sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            bgcolor: 'white',
                                            borderRadius: { xs: 1.5, sm: 2 },
                                            p: { xs: 1, sm: 1.5 },
                                            border: '2px solid',
                                            borderColor: 'grey.200',
                                            gap: { xs: 1, sm: 1.5 },
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                              borderColor: 'primary.main',
                                              boxShadow: 2
                                            }
                                          }}
                                        >
                                          {/* ✅ Image Box - ALWAYS SHOWS */}
                                          <Box
                                            sx={{
                                              width: { xs: 60, sm: 70 },
                                              height: { xs: 60, sm: 70 },
                                              borderRadius: { xs: 1.5, sm: 2 },
                                              flexShrink: 0,
                                              overflow: 'hidden',
                                              position: 'relative',
                                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                                              border: '2px solid',
                                              borderColor: alpha(theme.palette.primary.main, 0.2),
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                            }}
                                          >
                                            {productImageUrl ? (
                                              <Box
                                                component="img"
                                                src={productImageUrl}
                                                alt={item.ProductName}
                                                onError={(e) => {
                                                  console.error('Image failed to load:', productImageUrl);
                                                  e.target.style.display = 'none';
                                                  // Show fallback icon
                                                  const parent = e.target.parentElement;
                                                  if (parent && !parent.querySelector('.fallback-icon')) {
                                                    const icon = document.createElement('div');
                                                    icon.className = 'fallback-icon';
                                                    icon.innerHTML = '🖼️';
                                                    icon.style.fontSize = '32px';
                                                    parent.appendChild(icon);
                                                  }
                                                }}
                                                sx={{ 
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover',
                                                }}
                                              />
                                            ) : (
                                              <ImageIcon sx={{ 
                                                fontSize: { xs: 32, sm: 36 }, 
                                                color: 'primary.main',
                                                opacity: 0.4
                                              }} />
                                            )}
                                          </Box>

                                          {/* Product Details */}
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography 
                                              variant="body2" 
                                              fontWeight={600}
                                              sx={{ 
                                                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                                mb: 0.5,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: 1.3
                                              }}
                                            >
                                              {item.ProductName}
                                            </Typography>
                                            <Stack 
                                              direction="row" 
                                              spacing={{ xs: 1, sm: 1.5 }}
                                              alignItems="center"
                                              flexWrap="wrap"
                                            >
                                              <Chip
                                                label={`${t('Qty')}: ${item.Quantity}`}
                                                size="small"
                                                sx={{ 
                                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                  height: { xs: 22, sm: 24 },
                                                  bgcolor: alpha(theme.palette.info.main, 0.15),
                                                  color: 'info.main',
                                                  fontWeight: 600
                                                }}
                                              />
                                              <Typography 
                                                variant="body2" 
                                                color="primary"
                                                fontWeight={700}
                                                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                              >
                                                {item.Price} {order.Currency || 'YER'}
                                              </Typography>
                                            </Stack>
                                          </Box>
                                        </Paper>
                                      );
                                    })}
                                  </Stack>
                                )}
                              </Box>
                            );
                          })()}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
        )}
      </Grid>
    </Box>
  );
};

export default UserFeedbackDetails;